import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { CheckCircle, Heart, ArrowRight, Loader2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DonationSuccessPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const t = {
    fr: {
      loading: "Vérification de votre paiement...",
      success: "Merci pour votre don !",
      successDesc: "Votre générosité nous permet de poursuivre notre mission pour une Côte d'Ivoire solidaire.",
      failed: "Paiement non confirmé",
      failedDesc: "Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, contactez-nous.",
      amount: "Montant",
      backHome: "Retour à l'accueil",
      donateAgain: "Faire un autre don"
    },
    en: {
      loading: "Verifying your payment...",
      success: "Thank you for your donation!",
      successDesc: "Your generosity allows us to continue our mission for a solidary Côte d'Ivoire.",
      failed: "Payment not confirmed",
      failedDesc: "We could not confirm your payment. If you were charged, please contact us.",
      amount: "Amount",
      backHome: "Back to home",
      donateAgain: "Make another donation"
    },
    pt: {
      loading: "A verificar o seu pagamento...",
      success: "Obrigado pela sua doação!",
      successDesc: "A sua generosidade permite-nos continuar a nossa missão por uma Costa do Marfim solidária.",
      failed: "Pagamento não confirmado",
      failedDesc: "Não foi possível confirmar o seu pagamento. Se foi debitado, contacte-nos.",
      amount: "Montante",
      backHome: "Voltar ao início",
      donateAgain: "Fazer outra doação"
    }
  };

  const content = t[language] || t.fr;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/donations/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  };

  return (
    <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1A0F2E]' : 'bg-[#FAFAFA]'}`}>
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`max-w-lg mx-auto p-8 rounded-2xl text-center ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-xl`}
        >
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#5B2D8E] animate-spin" />
              <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                {content.loading}
              </h1>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0D9488]/10 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-[#0D9488]" />
              </div>
              <h1 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                {content.success}
              </h1>
              <p className={`mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                {content.successDesc}
              </p>
              
              {paymentInfo && (
                <div className={`p-4 rounded-xl mb-6 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{content.amount}</span>
                  <p className="text-3xl font-bold text-[#F7C948]">
                    {paymentInfo.amount}€
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/accueil"
                  className="flex-1 py-3 px-6 rounded-lg bg-[#5B2D8E] text-white font-medium flex items-center justify-center gap-2 hover:bg-[#4A2472] transition-colors"
                >
                  {content.backHome}
                </Link>
                <Link
                  to="/don"
                  className="flex-1 py-3 px-6 rounded-lg bg-[#F7C948] text-[#1A0F2E] font-medium flex items-center justify-center gap-2 hover:bg-[#FFD966] transition-colors"
                >
                  <Heart className="w-4 h-4" />
                  {content.donateAgain}
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                {content.failed}
              </h1>
              <p className={`mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                {content.failedDesc}
              </p>
              <Link
                to="/don"
                className="inline-flex items-center gap-2 py-3 px-6 rounded-lg bg-[#5B2D8E] text-white font-medium hover:bg-[#4A2472] transition-colors"
              >
                {content.donateAgain}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default DonationSuccessPage;
